import { loginRequest } from "../authConfig";

const GRAPH = "https://graph.microsoft.com/v1.0";
const SHAREPOINT_HOSTNAME = "westernfireproofing.sharepoint.com";
const SHAREPOINT_SITE_PATH = "/sites/ProjectDatabase";
const SITE_CACHE_KEY = "siteId";

const EXTRA_SCOPES = ["Files.ReadWrite.All", "Sites.Read.All", "Sites.ReadWrite.All"];

let siteIdCache = "";
const driveIdCache = {};

export async function getGraphToken(instance, account) {
  const scopes = Array.from(new Set([...(loginRequest.scopes || []), ...EXTRA_SCOPES]));
  try {
    const silent = await instance.acquireTokenSilent({ ...loginRequest, account, scopes });
    return silent.accessToken;
  } catch {
    const popup = await instance.loginPopup({ ...loginRequest, scopes });
    return popup.accessToken;
  }
}

function authHeaders(token, extra) {
  return { Authorization: `Bearer ${token}`, ...extra };
}

async function ensureSiteId(token) {
  if (siteIdCache) return siteIdCache;
  const cached = window.localStorage.getItem(SITE_CACHE_KEY);
  if (cached) {
    siteIdCache = cached;
    return cached;
  }
  const response = await fetch(
    `${GRAPH}/sites/${SHAREPOINT_HOSTNAME}:${SHAREPOINT_SITE_PATH}?$select=id`,
    { headers: authHeaders(token) }
  );
  if (!response.ok) throw new Error(`Couldn't open Project Database (${response.status})`);
  const json = await response.json();
  if (!json.id) throw new Error("Project Database site was not found");
  siteIdCache = json.id;
  window.localStorage.setItem(SITE_CACHE_KEY, json.id);
  return json.id;
}

async function ensureDriveId(token, library) {
  if (driveIdCache[library]) return driveIdCache[library];
  const siteId = await ensureSiteId(token);
  const response = await fetch(`${GRAPH}/sites/${siteId}/drives?$select=id,name,driveType`, {
    headers: authHeaders(token),
  });
  if (!response.ok) throw new Error(`Couldn't open the ${library} library (${response.status})`);
  const json = await response.json();
  const found = (json.value || []).find(
    (drive) => drive.driveType === "documentLibrary" && String(drive.name || "").toLowerCase() === library.toLowerCase()
  );
  if (!found?.id) throw new Error(`Document library '${library}' was not found`);
  driveIdCache[library] = found.id;
  return found.id;
}

async function ensurePath(token, driveId, segments) {
  const built = [];
  for (const segment of segments) {
    const parent = built.map(encodeURIComponent).join("/");
    built.push(segment);
    const next = built.map(encodeURIComponent).join("/");
    const check = await fetch(`${GRAPH}/drives/${driveId}/root:/${next}`, { headers: authHeaders(token) });
    if (check.ok) continue;
    if (check.status !== 404) throw new Error(`Couldn't open a SharePoint folder (${check.status})`);
    const url = parent
      ? `${GRAPH}/drives/${driveId}/root:/${parent}:/children`
      : `${GRAPH}/drives/${driveId}/root/children`;
    const created = await fetch(url, {
      method: "POST",
      headers: authHeaders(token, { "Content-Type": "application/json" }),
      body: JSON.stringify({
        name: segment,
        folder: {},
        "@microsoft.graph.conflictBehavior": "fail",
      }),
    });
    if (!created.ok && created.status !== 409) {
      throw new Error(`Couldn't create a SharePoint folder (${created.status})`);
    }
  }
}

export async function uploadProjectFile(token, { library, segments, fileName, blob }) {
  const driveId = await ensureDriveId(token, library);
  await ensurePath(token, driveId, segments);
  const rel = [...segments.map(encodeURIComponent), encodeURIComponent(fileName)].join("/");
  const response = await fetch(`${GRAPH}/drives/${driveId}/root:/${rel}:/content`, {
    method: "PUT",
    headers: authHeaders(token, { "Content-Type": blob.type || "application/octet-stream" }),
    body: blob,
  });
  if (!response.ok) throw new Error(`Upload to ${library} failed (${response.status})`);
  const item = await response.json();
  return {
    itemId: item.id,
    webUrl: item.webUrl || "",
    name: item.name || fileName,
  };
}

export async function deleteProjectItem(token, library, itemId) {
  if (!itemId) return;
  const driveId = await ensureDriveId(token, library);
  const response = await fetch(`${GRAPH}/drives/${driveId}/items/${itemId}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  if (!response.ok && response.status !== 404) {
    throw new Error(`Couldn't remove a SharePoint file (${response.status})`);
  }
}

export async function deleteProjectPath(token, library, segments) {
  const driveId = await ensureDriveId(token, library);
  const rel = segments.map(encodeURIComponent).join("/");
  const response = await fetch(`${GRAPH}/drives/${driveId}/root:/${rel}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  if (!response.ok && response.status !== 404) {
    throw new Error(`Couldn't remove a SharePoint folder (${response.status})`);
  }
}

export async function downloadProjectFile(token, library, itemId) {
  const driveId = await ensureDriveId(token, library);
  const response = await fetch(`${GRAPH}/drives/${driveId}/items/${itemId}/content`, {
    headers: authHeaders(token),
  });
  if (!response.ok) throw new Error(`Couldn't open a SharePoint file (${response.status})`);
  return response.blob();
}

export function dataUrlToBlob(dataUrl) {
  const match = /^data:(.*?);base64,(.*)$/.exec(String(dataUrl || ""));
  if (!match) throw new Error("Photo is not ready to upload");
  const binary = atob(match[2]);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: match[1] || "image/jpeg" });
}
